import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiPhone, FiMail, FiClock } from 'react-icons/fi';

const FooterContainer = styled.footer`
  background-color: #1C1511;
  color: #FFFFFF;
  padding: 3rem 0 1rem;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 3rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 3rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: var(--primary-color);
    font-size: 1.25rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  p {
    line-height: 1.6;
    color: #6B5E4E;
    margin-bottom: 0.75rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #FAF8F3;

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: var(--primary-color);
  }

  a {
    color: #FAF8F3;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #D4A017;
    }
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterLink = styled(Link)`
  color: #FAF8F3;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #D4A017;
  }
`;

const OpeningHours = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: #FAF8F3;

  .hours-row {
    display: flex;
    justify-content: space-between;
    gap: 2rem;

    .day {
      font-weight: 500;
    }
  }
`;

const FooterBottom = styled.div`
  padding-top: 2rem;
  border-top: 1px solid #E8DDD0;
  text-align: center;
  color: #6B5E4E;
  font-size: 0.875rem;

  @media (min-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;

  @media (min-width: 768px) {
    margin-top: 0;
  }

  a {
    color: #FAF8F3;
    transition: color 0.2s;

    &:hover {
      color: #D4A017;
    }
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          {/* Shop Information */}
          <FooterSection>
            <h3>{t('common.brandName')}</h3>
            <ContactInfo>
              <ContactItem>
                <FiMapPin />
                <div>
                  <a href="https://maps.app.goo.gl/nfNAa4mWgLX5KCvPA" target="_blank" rel="noopener noreferrer">
                    <strong>Papawcha Oxford & Hi Tea Bakery</strong><br />
                    6 Gloucester St<br />
                    Oxford OX1 2BN<br />
                    United Kingdom
                  </a>
                </div>
              </ContactItem>
              <ContactItem>
                <FiPhone />
                <a href="tel:+441865499688">+44 1865 499688</a>
              </ContactItem>
              <ContactItem>
                <FiMail />
                <a href="mailto:hiteaoxford@gmail.com">hiteaoxford@gmail.com</a>
              </ContactItem>
            </ContactInfo>
          </FooterSection>

          {/* Opening Hours */}
          <FooterSection>
            <h3>{t('footer.openingHours.title')}</h3>
            <OpeningHours>
              <ContactItem>
                <FiClock />
                <div style={{ minWidth: '180px' }}>
                  <div className="hours-row">
                    <span className="day">{t('footer.openingHours.monToSat')}</span>
                    <span>11:00 - 20:00</span>
                  </div>
                  <div className="hours-row">
                    <span className="day">{t('footer.openingHours.sunday')}</span>
                    <span>11:00 - 19:00</span>
                  </div>
                </div>
              </ContactItem>
            </OpeningHours>
          </FooterSection>

          {/* Quick Links */}
          <FooterSection>
            <h3>{t('footer.quickLinks.title')}</h3>
            <FooterLinks>
              <FooterLink to="/products" onClick={handleLinkClick}>{t('footer.quickLinks.ourProducts')}</FooterLink>
              <FooterLink to="/categories" onClick={handleLinkClick}>{t('footer.quickLinks.categories')}</FooterLink>
              <FooterLink to="/account" onClick={handleLinkClick}>{t('footer.quickLinks.myAccount')}</FooterLink>
              <FooterLink to="/orders" onClick={handleLinkClick}>{t('footer.quickLinks.trackOrder')}</FooterLink>
            </FooterLinks>
          </FooterSection>

          {/* Legal */}
          <FooterSection>
            <h3>{t('footer.legal.title')}</h3>
            <FooterLinks>
              <FooterLink to="/privacy-policy" onClick={handleLinkClick}>{t('footer.legal.privacyPolicy')}</FooterLink>
              <FooterLink to="/cookie-policy" onClick={handleLinkClick}>{t('footer.legal.cookiePolicy')}</FooterLink>
              <FooterLink to="/terms" onClick={handleLinkClick}>{t('footer.legal.termsAndConditions')}</FooterLink>
              <FooterLink to="/refund-policy" onClick={handleLinkClick}>{t('footer.legal.refundPolicy')}</FooterLink>
            </FooterLinks>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <div>
            {t('footer.copyright', { year: currentYear, brand: t('common.brandName') })}
          </div>
          <SocialLinks>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
              Facebook
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
              Instagram
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
              Twitter
            </a>
          </SocialLinks>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;